import React from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare, Users, Star, Briefcase, Video, Monitor, Check, ArrowRight } from 'lucide-react';
import { useBookingForm } from '../../hooks/useBookingForm';
import { BookingConfig, BookingFormProps } from '../../types/booking';
import { FormField } from './FormField';
import { DatePicker } from './DatePicker';
// import { CustomFieldRenderer } from './CustomFieldRenderer';
import { WizardLayout } from './WizardLayout';

export const BookingForm: React.FC<BookingFormProps> = ({
  config,
  onSubmissionSuccess,
  onSubmissionError,
  onFieldChange,
  onValidationError,
  className = '',
  style = {}
}) => {
  const {
    formData,
    customFieldData,
    errors,
    isSubmitting,
    isSubmitted,
    currentStep,
    availableTimeSlots,
    visibleCustomFields,
    updateField,
    updateCustomField,
    submitForm,
    resetForm,
    nextStep,
    previousStep,
    setCurrentStep,
    getFieldError,
  } = useBookingForm(config);

  const { theme, darkMode, layout } = config;

  // Define helper functions before they are used
  const getInputClasses = (error?: string) => {
    return `w-full pl-12 pr-4 py-3 rounded-lg border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 ${error
      ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
      : 'focus:ring-opacity-50'
      } ${darkMode
        ? 'bg-gray-800 text-white border-gray-600 hover:bg-gray-700 focus:border-blue-400 focus:ring-blue-400'
        : 'bg-white text-gray-900 border-gray-300 hover:bg-gray-50 focus:border-blue-500 focus:ring-blue-500'
      }`;
  };

  const getInputStyle = (error?: string) => ({
    borderColor: error ? theme.error : theme.border,
    '--focus-ring-color': theme.primary,
  } as React.CSSProperties);

  const handleFieldChange = (field: string, value: any) => {
    if (['name', 'email', 'phone', 'date', 'time', 'message', 'serviceType', 'partySize', 'specialRequests', 'preferredStaff', 'meetingType', 'selectedPricing'].includes(field)) {
      updateField(field as any, value);
    } else {
      updateCustomField(field, value);
    }

    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    try {
      const success = await submitForm();
      if (success) {
        // --- NEW: Push booking data to external API ---
        try {
          const apiKey = process.env.NEXT_PUBLIC_BOOKING_API_KEY;
          const apiUrl = process.env.NEXT_PUBLIC_BOOKING_API_URL || 'https://dash.agnuscloud.com/api/bookings';
          const payload = {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            date: formData.date,
            time: formData.time,
            meeting_type: formData.meetingType,
            message: formData.message || ''
          };
          await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'X-API-Key': apiKey || '',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
          });
        } catch (apiError) {
          // Optionally: handle/log API error (do not block local success)
          // console.error('External booking API error:', apiError);
        }
        // --- END NEW ---
        if (onSubmissionSuccess) {
          onSubmissionSuccess({ ...formData, ...customFieldData });
        }
      } else {
        if (onValidationError) {
          onValidationError(errors);
        }
      }
    } catch (error) {
      if (onSubmissionError) {
        onSubmissionError(error as Error);
      }
    }
  };

  // Helper function to split full name into first and last name
  const getDisplayName = (fullName: string) => {
    if (!fullName) return { firstName: '', lastName: '' };
    const nameParts = fullName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    return { firstName, lastName };
  };

  const { firstName, lastName } = getDisplayName(formData.name);

  // Success state
  if (isSubmitted) {
    return (
      <div
        className={`max-w-2xl mx-auto p-8 rounded-xl text-center ${className}`}
        style={{
          backgroundColor: darkMode ? theme.background : '#ffffff',
          border: `1px solid ${theme.border}`,
          ...style
        }}
      >
        <div
          className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
          style={{ backgroundColor: theme.success }}
        >
          <Check className="w-10 h-10 text-white" />
        </div>

        <h3
          className="text-2xl font-bold mb-4"
          style={{ color: theme.text }}
        >
          {config.localization.translations['success.title'] || 'Afspraak bevestigd!'}
        </h3>

        <div className={`p-6 rounded-lg mb-6 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <h4 className="font-semibold mb-4" style={{ color: theme.text }}>Jouw afspraak details:</h4>
          <div className="space-y-2 text-sm" style={{ color: theme.textSecondary }}>
            <p><strong>Voornaam:</strong> {firstName}</p>
            <p><strong>Achternaam:</strong> {lastName}</p>
            <p><strong>E-mail:</strong> {formData.email}</p>
            <p><strong>Telefoon:</strong> {formData.phone}</p>
            <p><strong>Datum:</strong> {formData.date}</p>
            <p><strong>Tijd:</strong> {formData.time}</p>
            <p><strong>Type:</strong> {formData.meetingType === 'video-call' ? 'Online gesprek' : 'Op locatie'}</p>
          </div>
        </div>



        <p
          className="mb-6"
          style={{ color: theme.textSecondary }}
        >
          {config.submission.successMessage || config.localization.translations['success.message'] ||
            'Bedankt voor uw afspraak! We nemen zo spoedig mogelijk contact met u op.'}
        </p>

        <button
          onClick={resetForm}
          className="px-8 py-3 rounded-lg font-medium transition-colors duration-200"
          style={{
            backgroundColor: theme.primary,
            color: 'white',
          }}
        >
          Nieuwe afspraak maken
        </button>
      </div>
    );
  }

  const enabledSections = config.sections.filter(s => s.enabled).sort((a, b) => a.order - b.order);

  // Wizard layout
  if (layout === 'wizard' && config.features.multiStep) {
    const currentSection = enabledSections[currentStep];

    return (
      <WizardLayout
        sections={enabledSections}
        currentSection={currentStep}
        onSectionChange={setCurrentStep}
        config={config}
        onNext={nextStep}
        onPrevious={previousStep}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      >
        <div className="space-y-6">
          {currentSection?.fields.map(fieldId => renderField(fieldId))}
        </div>
      </WizardLayout>
    );
  }

  function renderField(fieldId: string) {
    switch (fieldId) {
      case 'hero':
        return (
          <div key="hero" className="text-center space-y-8">
            {config.heroImage && (
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src={config.heroImage}
                  alt="Consultation"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
            )}

            <div className="space-y-4">
              <h1
                className="text-3xl font-bold"
                style={{ color: theme.text }}
              >
                {config.title}
              </h1>
              <p
                className="text-lg"
                style={{ color: theme.textSecondary }}
              >
                {config.description}
              </p>
            </div>
          </div>
        );

      case 'name':
        return (
          <FormField
            key="name"
            label={config.localization.translations['field.name'] || 'Naam'}
            required
            config={config}
            error={getFieldError('name')}
          >
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: theme.textSecondary }} />
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                className={getInputClasses(getFieldError('name'))}
                style={getInputStyle(getFieldError('name'))}
                placeholder={config.localization.translations['field.namePlaceholder'] || 'Voer je volledige naam in'}
              />
            </div>
          </FormField>
        );

      case 'email':
        return (
          <FormField
            key="email"
            label={config.localization.translations['field.email'] || 'E-mailadres'}
            required
            config={config}
            error={getFieldError('email')}
          >
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: theme.textSecondary }} />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                className={getInputClasses(getFieldError('email'))}
                style={getInputStyle(getFieldError('email'))}
                placeholder={config.localization.translations['field.emailPlaceholder'] || 'je@email.com'}
              />
            </div>
          </FormField>
        );

      case 'phone':
        return (
          <FormField
            key="phone"
            label={config.localization.translations['field.phone'] || 'Telefoonnummer'}
            required
            config={config}
            error={getFieldError('phone')}
          >
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                style={{ color: theme.textSecondary }} />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleFieldChange('phone', e.target.value)}
                className={getInputClasses(getFieldError('phone'))}
                style={getInputStyle(getFieldError('phone'))}
                placeholder={config.localization.translations['field.phonePlaceholder'] || '+31 6 12345678'}
              />
            </div>
          </FormField>
        );

      case 'date':
        return (
          <FormField
            key="date-time"
            label={config.localization.translations['field.date'] || 'Selecteer een datum en tijd'}
            required
            config={config}
          >
            <DatePicker
              value={formData.date}
              timeValue={formData.time}
              timeSlots={availableTimeSlots}
              onChange={(date) => handleFieldChange('date', date)}
              onTimeSelect={(time) => handleFieldChange('time', time)}
              config={config}
              error={getFieldError('date')}
              timeError={getFieldError('time')}
            />
          </FormField>
        );

      case 'time':
        // Skip rendering time separately since it's combined with date
        return null;

      case 'meetingType':
        if (!config.fields.meetingType) return null;
        return (
          <FormField
            key="meetingType"
            label={config.localization.translations['field.meetingType'] || 'Type afspraak'}
            required
            config={config}
            error={getFieldError('meetingType')}
          >
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleFieldChange('meetingType', 'in-person')}
                className={`p-4 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 flex items-center gap-3 ${formData.meetingType === 'in-person'
                  ? 'text-white shadow-md'
                  : `border hover:shadow-sm ${darkMode
                    ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`
                  }`}
                style={{
                  backgroundColor: formData.meetingType === 'in-person' ? theme.primary : undefined,
                  borderColor: formData.meetingType === 'in-person' ? theme.primary : theme.border,
                  '--focus-ring-color': theme.primary,
                } as React.CSSProperties}
              >
                <Monitor className="w-5 h-5" />
                <span>
                  {config.localization.translations['meetingType.inPerson'] || 'Op locatie'}
                </span>
              </button>

              <button
                type="button"
                onClick={() => handleFieldChange('meetingType', 'video-call')}
                className={`p-4 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 flex items-center gap-3 ${formData.meetingType === 'video-call'
                  ? 'text-white shadow-md'
                  : `border hover:shadow-sm ${darkMode
                    ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`
                  }`}
                style={{
                  backgroundColor: formData.meetingType === 'video-call' ? theme.primary : undefined,
                  borderColor: formData.meetingType === 'video-call' ? theme.primary : theme.border,
                  '--focus-ring-color': theme.primary,
                } as React.CSSProperties}
              >
                <Video className="w-5 h-5" />
                <span>
                  {config.localization.translations['meetingType.online'] || 'Online gesprek'}
                </span>
              </button>
            </div>
          </FormField>
        );

      case 'message':
        return (
          <FormField
            key="message"
            label={config.localization.translations['field.message'] || 'Bericht'}
            config={config}
          >
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 w-5 h-5"
                style={{ color: theme.textSecondary }} />
              <textarea
                value={formData.message}
                onChange={(e) => handleFieldChange('message', e.target.value)}
                rows={4}
                className={`${getInputClasses()} resize-none`}
                style={getInputStyle()}
                placeholder={config.localization.translations['field.messagePlaceholder'] || 'Vertel ons meer over je behoeften...'}
              />
            </div>
          </FormField>
        );

      case 'pricing':
        if (!config.pricing?.enabled) return null;
        return (
          <div key="pricing" className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-2" style={{ color: theme.text }}>
                Kies je package
              </h3>
              <p style={{ color: theme.textSecondary }}>
                Selecteer het package dat het beste bij je behoeften past
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {config.pricing.options
                .filter(option => option.enabled)
                .map(option => (
                  <div
                    key={option.id}
                    className={`relative p-6 rounded-xl transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-opacity-50 ${formData.selectedPricing === option.id
                      ? 'text-white shadow-lg'
                      : `border hover:shadow-sm ${darkMode
                        ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`
                      } ${option.popular ? 'ring-2 ring-opacity-50' : ''}`}
                    style={{
                      backgroundColor: formData.selectedPricing === option.id ? theme.primary : undefined,
                      borderColor: formData.selectedPricing === option.id ? theme.primary : theme.border,
                      '--focus-ring-color': theme.primary,
                      ringColor: option.popular ? theme.primary : undefined,
                    } as React.CSSProperties}
                    onClick={() => handleFieldChange('selectedPricing', option.id)}
                  >
                    {option.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span
                          className="text-white px-3 py-1 rounded-full text-sm font-medium shadow-md"
                          style={{ backgroundColor: theme.primary }}
                        >
                          Populair
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-4">
                      <h4 className="text-xl font-bold mb-2" style={{ color: theme.text }}>
                        {option.name}
                      </h4>
                      <div className="text-3xl font-bold" style={{ color: theme.primary }}>
                        {config.currency}{option.price}
                      </div>
                      {option.description && (
                        <p className="text-sm mt-2" style={{ color: theme.textSecondary }}>
                          {option.description}
                        </p>
                      )}
                    </div>

                    {option.features && (
                      <ul className="space-y-2">
                        {option.features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span style={{ color: theme.text }}>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="absolute top-4 right-4">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${formData.selectedPricing === option.id
                          ? 'text-white shadow-md'
                          : 'border-gray-300'
                          }`}
                        style={{
                          backgroundColor: formData.selectedPricing === option.id ? theme.primary : undefined,
                          borderColor: formData.selectedPricing === option.id ? theme.primary : theme.border,
                        } as React.CSSProperties}
                      >
                        {formData.selectedPricing === option.id && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        );

      case 'confirmation':
        const selectedPricing = config.pricing?.options?.find(p => p.id === formData.selectedPricing);
        return (
          <div key="confirmation" className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-2" style={{ color: theme.text }}>
                Bevestig je afspraak
              </h3>
              <p style={{ color: theme.textSecondary }}>
                Controleer je gegevens voordat je de afspraak bevestigt
              </p>
            </div>

            <div className={`p-6 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <h4 className="font-semibold mb-3" style={{ color: theme.text }}>Afspraak details</h4>
                  <div className="space-y-2 text-sm" style={{ color: theme.textSecondary }}>
                    <p><strong>Datum:</strong> {formData.date}</p>
                    <p><strong>Tijd:</strong> {formData.time}</p>
                    <p><strong>Type:</strong> {formData.meetingType === 'video-call' ? 'Online gesprek' : 'Op locatie'}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3" style={{ color: theme.text }}>Contact gegevens</h4>
                  <div className="space-y-2 text-sm" style={{ color: theme.textSecondary }}>
                    <p><strong>Voornaam:</strong> {firstName}</p>
                    <p><strong>Achternaam:</strong> {lastName}</p>
                    <p><strong>Email:</strong> {formData.email}</p>
                    <p><strong>Telefoon:</strong> {formData.phone}</p>
                  </div>
                </div>
              </div>

              {selectedPricing && (
                <div className="mt-6 pt-6 border-t" style={{ borderColor: theme.border }}>
                  <h4 className="font-semibold mb-3" style={{ color: theme.text }}>Geselecteerd package</h4>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium" style={{ color: theme.text }}>{selectedPricing.name}</p>
                      <p className="text-sm" style={{ color: theme.textSecondary }}>{selectedPricing.description}</p>
                    </div>
                    <div className="text-xl font-bold" style={{ color: theme.primary }}>
                      {config.currency}{selectedPricing.price}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        // Handle custom fields - temporarily disabled
        /*
        const customField = visibleCustomFields.find(f => f.id === fieldId);
        if (customField) {
          return (
            <CustomFieldRenderer
              key={fieldId}
              field={customField}
              value={customFieldData[fieldId]}
              onChange={(value) => handleFieldChange(fieldId, value)}
              config={config}
              error={getFieldError(fieldId)}
            />
          );
        }
        */
        return null;
    }
  }

  // Regular layouts (fallback)
  const containerStyle = {
    backgroundColor: darkMode ? theme.background : '#ffffff',
    border: `1px solid ${theme.border}`,
    fontFamily: config.typography.fontFamily,
    ...style
  };

  return (
    <div
      className={`max-w-4xl mx-auto p-8 rounded-xl shadow-lg ${className}`}
      style={containerStyle}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {enabledSections.map(section => (
          <div key={section.id} className="space-y-6">
            {section.title && section.id !== 'hero' && (
              <div className="border-b pb-4" style={{ borderColor: theme.border }}>
                <h3
                  className="text-lg font-semibold"
                  style={{ color: theme.text }}
                >
                  {section.title}
                </h3>
                {section.description && (
                  <p
                    className="text-sm mt-1"
                    style={{ color: theme.textSecondary }}
                  >
                    {section.description}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-6">
              {section.fields.map(fieldId => renderField(fieldId))}
            </div>
          </div>
        ))}

        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 flex items-center justify-center gap-2 ${isSubmitting
            ? 'opacity-75 cursor-not-allowed'
            : 'hover:shadow-lg transform hover:-translate-y-0.5'
            }`}
          style={{
            backgroundColor: theme.primary,
            '--focus-ring-color': theme.primary,
          } as React.CSSProperties}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Bezig met boeken...
            </>
          ) : (
            <>
              {config.localization.translations['button.submit'] || 'Bevestig afspraak'}
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </form>
    </div>
  );
};